import { io } from 'socket.io-client';

// Determine socket URL based on current protocol
// If site is HTTPS, socket must also be secure (wss/https)
const getSocketUrl = () => {
  // Check for environment variable first
  if (import.meta.env.VITE_SOCKET_URL) {
    return import.meta.env.VITE_SOCKET_URL;
  }

  // Use same protocol as current page to avoid mixed content
  const isSecure = window.location.protocol === 'https:';
  const protocol = isSecure ? 'https' : 'http';

  return `${protocol}://custom-dev.onlinetestingserver.com:9997`;
};

const SOCKET_URL = getSocketUrl();


class SocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.pendingEmits = [];
  }

  connect(token) {
    if (this.socket?.connected) {
      console.log('Socket already connected:', this.socket.id);
      return this.socket;
    }

    console.log('=== SOCKET CONNECTING ===');
    console.log('Socket URL:', SOCKET_URL);
    console.log('Token:', token ? 'Present' : 'Missing');

    try {
      this.socket = io(SOCKET_URL, {
        transports: ['polling', 'websocket'],
        auth: {
          token: token
        },
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionAttempts: 10,
        timeout: 20000,
        forceNew: true
      });

      this.socket.on('connect', () => {
        console.log('✅ Socket connected:', this.socket.id);
        this.isConnected = true;

        // Process any pending emits
        if (this.pendingEmits.length > 0) {
          console.log('Processing', this.pendingEmits.length, 'pending emits');
          this.pendingEmits.forEach(({ event, data }) => {
            console.log('Processing pending emit:', event, data);
            this.socket.emit(event, data);
          });
          this.pendingEmits = [];
        }
      });

      this.socket.on('disconnect', (reason) => {
        console.log('❌ Socket disconnected:', reason);
        this.isConnected = false;
      });

      this.socket.on('connect_error', (error) => {
        console.error('❌ Socket connection error:', error.message);
        console.error('Error details:', error);
      });

      this.socket.on('error', (error) => {
        console.error('❌ Socket error:', error);
      });

      // Debug: Log all incoming events
      this.socket.onAny((eventName, ...args) => {
        console.log('📩 Socket received event:', eventName, args);
      });

      return this.socket;
    } catch (error) {
      console.error('❌ Failed to create socket:', error);
      return null;
    }
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
    }
  }

  emit(event, data) {
    if (this.socket?.connected) {
      console.log('Socket emitting:', event, data);
      this.socket.emit(event, data);
    } else {
      console.log('Socket not connected, queuing emit:', event, data);
      this.pendingEmits.push({ event, data });
    }
  }

  on(event, callback) {
    if (this.socket) {
      console.log('Socket listening on:', event);
      this.socket.on(event, callback);
    }
  }

  off(event, callback) {
    if (this.socket) {
      this.socket.off(event, callback);
    }
  }

  // Chat specific methods
  joinChat(chatId) {
    this.emit('join_chat', { chat_id: chatId });
  }

  leaveChat(chatId) {
    this.emit('leave_chat', { chat_id: chatId });
  }

  sendMessage(data) {
    this.emit('send_message', data);
  }

  onNewMessage(callback) {
    this.on('new_message', callback);
  }

  offNewMessage(callback) {
    this.off('new_message', callback);
  }

  onMessageSent(callback) {
    this.on('message_sent', callback);
  }

  offMessageSent(callback) {
    this.off('message_sent', callback);
  }

  onChatListUpdate(callback) {
    this.on('chat_list_update', callback);
  }

  offChatListUpdate(callback) {
    this.off('chat_list_update', callback);
  }

  onTyping(callback) {
    this.on('user_typing', callback);
  }

  offTyping(callback) {
    this.off('user_typing', callback);
  }

  emitTyping(chatId) {
    this.emit('typing', { chat_id: chatId });
  }

  emitStopTyping(chatId) {
    this.emit('stop_typing', { chat_id: chatId });
  }

  // Watch Together methods
  // Room format: wt-{chatId} (e.g., wt-28)
  // Event: watchTogetherEvent
  // Listener: wt-{chatId}

  joinWatchRoom(chatId) {
    const roomName = `wt-${chatId}`;
    console.log('=== JOINING WATCH ROOM ===');
    console.log('Room:', roomName);
    console.log('Socket connected:', this.socket?.connected);

    this.emit('watchTogetherEvent', {
      action: 'join',
      room: roomName,
      chat_id: chatId
    });
  }

  leaveWatchRoom(chatId) {
    const roomName = `wt-${chatId}`;
    console.log('=== LEAVING WATCH ROOM ===');

    this.emit('watchTogetherEvent', {
      action: 'leave',
      room: roomName,
      chat_id: chatId
    });
  }

  syncVideoState(chatId, data) {
    const roomName = `wt-${chatId}`;
    this.emit('watchTogetherEvent', {
      action: 'sync',
      room: roomName,
      chat_id: chatId,
      ...data
    });
  }

  changeVideo(chatId, videoId, userId) {
    const roomName = `wt-${chatId}`;
    console.log('=== CHANGING VIDEO ===');
    console.log('Video ID:', videoId);
    console.log('User ID:', userId);

    this.emit('watchTogetherEvent', {
      action: 'change_video',
      room: roomName,
      chat_id: chatId,
      video_id: videoId,
      user_id: userId
    });
  }

  sendWatchMessage(chatId, message, userId, userName, userImage) {
    const roomName = `wt-${chatId}`;
    console.log('=== SENDING WATCH MESSAGE ===');
    console.log('Message:', message);

    this.emit('watchTogetherEvent', {
      action: 'message',
      room: roomName,
      chat_id: chatId,
      message: message,
      user_id: userId,
      user_name: userName,
      user_image: userImage,
      timestamp: new Date().toISOString()
    });
  }

  // Listen to watchTogetherEvent (for broadcast back from server)
  onWatchTogetherEvent(callback) {
    this.on('watchTogetherEvent', callback);
  }

  offWatchTogetherEvent(callback) {
    this.off('watchTogetherEvent', callback);
  }

  // Listen to room-specific events (wt-{chatId})
  // The server broadcasts on this channel
  onWatchRoom(chatId, callback) {
    const roomName = `wt-${chatId}`;
    console.log('=== LISTENING ON ROOM ===', roomName);
    this.on(roomName, callback);
  }

  offWatchRoom(chatId, callback) {
    const roomName = `wt-${chatId}`;
    this.off(roomName, callback);
  }

  // Check if socket is connected
  getConnectionStatus() {
    return {
      connected: this.socket?.connected || false,
      id: this.socket?.id || null
    };
  }
}

const socketService = new SocketService();

export default socketService;
